import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getTwinBrandMix,
  refreshTwinAssetProposals,
  type TwinBrandMix,
} from '@repo/front-end-services';

/**
 * The Equipment step's two calls to the brand-mix routes (`equipment-page-stage`, spec R16–R17;
 * epic ledger row 53).
 *
 * **Once per visit, the stored guesses are refreshed.** Rows imported before guesses were stored
 * have none; `POST twin-assets/proposals/refresh` re-derives every unmatched row's guess from the
 * importer's clues. It runs when the step opens on a twin whose register has been read and has
 * rows, and not again until the step is left and re-entered or the twin changes. When it changed
 * anything the register is re-read. A failure is logged and changes nothing on screen: the guesses
 * stay as they were. It is not an operator's change, so it never drives the *Draft saved* tag.
 *
 * **Then the brand mix is read** — after the refresh settles, also when it failed; again after
 * every register write (`writeSeq`); and on the card's Retry. A newer read supersedes an older
 * one: the older request is aborted and a late answer is dropped, since an aborted fetch can still
 * resolve first. A failed read is the card's errored state, never an empty mix.
 *
 * Bounds: both calls go through `@repo/front-end-services`' clients, which carry the repo's
 * declared timeouts (`MIDDLEWARE_WRITE_TIMEOUT_MS` on the POST, the request timeout on the GET).
 * Zero automatic retries — the card's Retry is the person's.
 */
export type EquipmentBrandMixState = 'loading' | 'ready' | 'error';

export interface EquipmentBrandMixArgs {
  /** Step 2 is on screen. */
  active: boolean;
  twinId: number | null | undefined;
  /** The register has been read and has rows. */
  ready: boolean;
  /** The address names two different twins: never call, as `reloadRegister` refuses. */
  conflicted: boolean;
  baseUrl: string;
  getToken: () => Promise<string>;
  reloadRegister: (options?: { afterWrite?: boolean }) => void;
  /** Bumped after each successful register write: the mix is read again. */
  writeSeq: number;
}

export interface EquipmentBrandMix {
  mix: TwinBrandMix | null;
  state: EquipmentBrandMixState;
  retry: () => void;
}

const reasonOf = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export const useEquipmentBrandMix = ({
  active,
  twinId,
  ready,
  conflicted,
  baseUrl,
  getToken,
  reloadRegister,
  writeSeq,
}: EquipmentBrandMixArgs): EquipmentBrandMix => {
  const [mix, setMix] = useState<TwinBrandMix | null>(null);
  const [state, setState] = useState<EquipmentBrandMixState>('loading');

  // Held in refs: the effects below must not re-fire because a caller passed a fresh function.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const reloadRef = useRef(reloadRegister);
  reloadRef.current = reloadRegister;

  /** The twin this visit has refreshed, or null — the once-per-visit guard. */
  const refreshedFor = useRef<number | null>(null);
  const readSeq = useRef(0);
  const controller = useRef<AbortController | null>(null);

  const readMix = useCallback(
    async (forTwin: number) => {
      const seq = ++readSeq.current;
      controller.current?.abort();
      const own = new AbortController();
      controller.current = own;
      try {
        const token = await getTokenRef.current();
        const answer = await getTwinBrandMix(baseUrl, token, forTwin, own.signal);
        if (seq !== readSeq.current) return;
        setMix(answer);
        setState('ready');
      } catch (error) {
        if (seq !== readSeq.current) return;
        // The reason only — never the raw error, whose request config carries the bearer token.
        console.warn('[equipment] brand mix read failed', {
          twinId: forTwin,
          reason: reasonOf(error),
        });
        setState('error');
      }
    },
    [baseUrl],
  );

  // A different twin is a different mix: nothing of the last one may show.
  useEffect(() => {
    readSeq.current += 1;
    controller.current?.abort();
    setMix(null);
    setState('loading');
    refreshedFor.current = null;
  }, [twinId]);

  // Leaving the step ends the visit.
  useEffect(() => {
    if (!active) refreshedFor.current = null;
  }, [active]);

  useEffect(() => {
    if (!active || !ready || conflicted || twinId == null) return;
    if (refreshedFor.current === twinId) return;
    refreshedFor.current = twinId;
    const forTwin = twinId;
    void (async () => {
      try {
        const token = await getTokenRef.current();
        const result = await refreshTwinAssetProposals(baseUrl, token, { twinId: forTwin });
        if (result.changed + result.cleared > 0) reloadRef.current({ afterWrite: true });
      } catch (error) {
        console.warn('[equipment] guess refresh failed', {
          twinId: forTwin,
          reason: reasonOf(error),
        });
      }
      if (refreshedFor.current === forTwin) await readMix(forTwin);
    })();
  }, [active, ready, conflicted, twinId, baseUrl, readMix]);

  // After a write, read the mix again — once this visit has had its first read.
  const lastWriteSeq = useRef(writeSeq);
  useEffect(() => {
    if (lastWriteSeq.current === writeSeq) return;
    lastWriteSeq.current = writeSeq;
    if (!active || conflicted || twinId == null || refreshedFor.current !== twinId) return;
    void readMix(twinId);
  }, [writeSeq, active, conflicted, twinId, readMix]);

  // Unmounting supersedes the read in flight, so its cancellation is not logged as a failure.
  useEffect(
    () => () => {
      readSeq.current += 1;
      controller.current?.abort();
    },
    [],
  );

  const retry = useCallback(() => {
    if (twinId == null || conflicted) return;
    setState('loading');
    void readMix(twinId);
  }, [twinId, conflicted, readMix]);

  return { mix, state, retry };
};
